<script lang="ts" setup>
import { ref, PropType, watch } from "vue";
import {
  useUpdateDocumentSpaceNameMutation,
  useUpdateDocumentSpaceMessageMutation,
  DocumentSpace,
} from "@/graphql/operations";
import { EditableInput, Modal } from "@/components";
import { onClickOutside } from "@vueuse/core";
import { onMounted } from "vue";
import { onBeforeRouteUpdate } from "vue-router";
import { onBeforeUnmount } from "vue";

const props = defineProps({
  documentSpace: {
    type: Object as PropType<DocumentSpace>,
    required: true,
  },
});

const emit = defineEmits(["success"]);

const textElem = ref();
const textArea = ref<HTMLTextAreaElement>();

const name = ref<string>(props.documentSpace.name || "");
const currentName = ref<string>(props.documentSpace.name || "");
const personalMessage = ref<string>(props.documentSpace.personalMessage || "");
const currentPersonalMessage = ref<string>(
  props.documentSpace.personalMessage || "",
);

watch(props, () => {
  if (props.documentSpace.name) {
    if (props.documentSpace.name !== name.value) {
      if (currentName.value === name.value) {
        currentName.value = props.documentSpace.name;
      }
      name.value = props.documentSpace.name;
    }
  }

  const nextPersonalMessage = props.documentSpace.personalMessage ?? "";
  if (nextPersonalMessage !== personalMessage.value) {
    if (currentPersonalMessage.value === personalMessage.value) {
      currentPersonalMessage.value = nextPersonalMessage;
    }

    personalMessage.value = nextPersonalMessage;
  }
});

const saveError = ref(false);
const { executeMutation: executeUpdateName, fetching: savingName } =
  useUpdateDocumentSpaceNameMutation();
const validateName = async (value: string) => {
  if (!value.length || savingName.value) return;
  saveError.value = false;
  const result = await executeUpdateName({
    id: props.documentSpace.id,
    name: value,
  });
  if (result.error || !result.data?.updateDocumentSpace) {
    saveError.value = true;
    return;
  }
  editMode.value = false;
  emit("success");
};

const { executeMutation: executeUpdateMessage, fetching: savingMessage } =
  useUpdateDocumentSpaceMessageMutation();
const updateMessage = async () => {
  if (savingMessage.value) return;
  saveError.value = false;
  const result = await executeUpdateMessage({
    id: props.documentSpace.id,
    message: currentPersonalMessage.value || "",
  });
  if (result.error || !result.data?.updateDocumentSpace) {
    saveError.value = true;
    return;
  }
  emit("success");
  unfocusText();
  displayConfirmation.value = false;
};

const editMode = ref(false);

const cancel = () => {
  if (savingMessage.value) return;
  saveError.value = false;
  currentPersonalMessage.value = personalMessage.value;
  unfocusText();
  displayConfirmation.value = false;
};

const unfocusText = () => {
  textArea.value?.focus();
  textElem.value.classList.remove("active");
};

const focusText = () => {
  textElem.value.classList.add("active");
};

onClickOutside(
  textElem,
  () => {
    if (currentPersonalMessage.value !== personalMessage.value) {
      displayConfirmation.value = true;
    }
  },
  {
    ignore: ['[data-test="personal-message-unsaved-modal"]'],
  },
);

const displayConfirmation = ref(false);

const dismissConfirmation = () => {
  if (savingMessage.value) return;
  displayConfirmation.value = false;
  textArea.value?.focus();
};

const handleBeforeUnload = (event: BeforeUnloadEvent) => {
  if (currentPersonalMessage.value !== personalMessage.value) {
    event.preventDefault();
    return "";
  }
};

onMounted(() => {
  window.addEventListener("beforeunload", handleBeforeUnload);
});

onBeforeUnmount(() => {
  window.removeEventListener("beforeunload", handleBeforeUnload);
});
</script>

<template>
  <div class="texts">
    <p v-if="saveError" role="alert" class="save_error">{{ $t("error") }}</p>
    <div class="inner">
      <EditableInput
        v-if="editMode"
        :value="currentName"
        :inert="savingName"
        :aria-busy="savingName"
        @submit="validateName"
        @cancel="!savingName && (editMode = false)"
      />
      <button
        v-else
        type="button"
        class="container clickable"
        :aria-label="$t('documentSpace.sections.edit')"
        @click="editMode = true"
      >
        <h2>
          {{ currentName }}
        </h2>
        <span class="material-symbols-outlined" aria-hidden="true"> edit </span>
      </button>
    </div>

    <div class="inner">
      <form @submit.prevent ref="textElem" @focusin="focusText">
        <textarea
          ref="textArea"
          v-model="currentPersonalMessage"
          :disabled="savingMessage"
          class="textarea"
          :placeholder="$t('documentSpace.personalMessage.placeholder')"
        />
        <div class="icons">
          <button
            type="button"
            class="icon_button material-symbols-outlined icon primary"
            data-test="personal-message-save"
            :aria-label="$t('documentSpace.personalMessage.save')"
            @click.stop="updateMessage"
            :disabled="
              savingMessage || currentPersonalMessage === personalMessage
            "
          >
            check
          </button>
          <button
            type="button"
            class="icon_button material-symbols-outlined icon"
            data-test="personal-message-cancel"
            :disabled="savingMessage"
            :aria-label="$t('cancel')"
            @click.stop="cancel"
          >
            close
          </button>
        </div>
      </form>
    </div>
  </div>

  <Modal
    :active="displayConfirmation"
    :close-disabled="savingMessage"
    data-test="personal-message-unsaved-modal"
    @close="dismissConfirmation"
  >
    <template #body>
      {{ $t("documentSpace.personalMessage.unsavedChanges") }}
      <p v-if="saveError" role="alert" class="save_error">{{ $t("error") }}</p>
    </template>
    <template #footer>
      <div class="buttons">
        <button class="button" :disabled="savingMessage" @click="cancel">
          {{ $t("documentSpace.personalMessage.discard") }}
        </button>
        <button
          class="button primary"
          :disabled="savingMessage"
          @click="updateMessage"
        >
          {{ $t("documentSpace.personalMessage.save") }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<style lang="scss" scoped>
.save_error {
  margin: 1em;
  padding: 0.75em;
  color: var(--classic-error-text, #9f3a38);
  background: var(--classic-error-surface, #fff6f6);
}

.texts {
  margin: 0.5em 0 0 0;
}

.inner {
  padding: 0 1em;
  margin-top: 0.5em;
}

textarea {
  max-width: 100%;
  min-height: 10em;
  margin-bottom: 5px;
}

.container {
  display: flex;
  align-items: center;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font-family: inherit;
}

h2 {
  line-height: 2.8rem;
  font-size: 1.2rem;
  margin: 0;
  font-weight: 500;
}

.material-symbols-outlined {
  font-size: 1rem;
  padding: 0.5em;
}

.clickable:hover {
  cursor: pointer;
}

form {
  position: relative;
}

form .icons {
  position: absolute;
  bottom: 0.6em;
  right: 0.6em;
  display: none;
}

form .icons .icon {
  font-size: 1.7rem;
  padding: 0;
}

.icon_button {
  border: 0;
  background: transparent;
  color: inherit;
}

.icon.primary {
  color: var(--classic-accent, $color-secondary);
}

.icon:hover {
  cursor: pointer;
}

form.active .icons {
  display: block;
}

.icon:disabled {
  opacity: 0.5;
  pointer-events: none;
}
</style>
